import { CSSProperties, FunctionComponent } from 'react'
import { useQuery } from '@apollo/client'
import { Tooltip, TooltipProps } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { GET_STAFF_PREPARATION_TASKS_BY_DAYCARE } from '@/modules/API/staff/staffPreparationTask'
import { StaffPreparationTask } from '@/types/StaffPreparationTask'
import { WorkType } from '@/types/work'
import { defineWorkTypeStyle, defineWorkTypeToCalendarEventType } from '@/container/planning/utils/eventsWorks'
import styles from './workPreview.module.scss'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'

interface WorkPreviewProps {
    label: string
    start: string
    end: string
    opening: string
    closing: string
    workType: string
    hasPause: boolean
    pauseStart: string
    pauseEnd: string
    preparationBefore: string[]
    preparationAfter: string[]
    daycareId?: number
}

interface Segment {
    start: number
    end: number
    label: string
    color: string
}

const previewTooltipProps: Partial<TooltipProps> = {
    followCursor: true,
    disableInteractive: true,
    arrow: true,
    placement: 'top',
    enterDelay: 100,
    enterNextDelay: 0,
    slotProps: {
        tooltip: { className: styles.Tooltip },
        arrow: { className: styles.TooltipArrow }
    }
}

function minutes(value: string): number {
    return /^([01]\d|2[0-3]):[0-5]\d$/.test(value) ? Number(value.slice(0, 2)) * 60 + Number(value.slice(3)) : NaN
}

function time(value: number): string {
    const normalized: number = ((value % 1440) + 1440) % 1440
    return `${Math.floor(normalized / 60)
        .toString()
        .padStart(2, '0')}:${(normalized % 60).toString().padStart(2, '0')}`
}

const WorkPreview: FunctionComponent<WorkPreviewProps> = (props) => {
    const { t } = useTranslation()
    const needsTasks: boolean = !!(props.preparationBefore.length || props.preparationAfter.length)
    // TODO: bring back refetch
    const { data, loading, error } = useQuery(GET_STAFF_PREPARATION_TASKS_BY_DAYCARE, {
        variables: { daycareId: props.daycareId },
        skip: !needsTasks
    })

    // TODO: verify how to correct this
    // useEffect(() => {
    //     if (needsTasks) void refetch()
    // }, [props.refetchTrigger])

    const tasks: StaffPreparationTask[] = data?.getStaffPreparationTasksByDaycare || []
    const before: StaffPreparationTask[] = props.preparationBefore
        .map((id) => tasks.find((task) => task.id.toString() === id))
        .filter((task): task is StaffPreparationTask => !!task)
    const after: StaffPreparationTask[] = props.preparationAfter
        .map((id) => tasks.find((task) => task.id.toString() === id))
        .filter((task): task is StaffPreparationTask => !!task)
    const start: number = minutes(props.start)
    const end: number = minutes(props.end)
    const pauseStart: number = minutes(props.pauseStart)
    const pauseEnd: number = minutes(props.pauseEnd)
    const valid: boolean =
        Number.isFinite(start) &&
        Number.isFinite(end) &&
        end > start &&
        (!props.hasPause || (pauseStart >= start && pauseEnd > pauseStart && pauseEnd <= end))
    const workColor: string = `${styles.Work} ${defineWorkTypeStyle(defineWorkTypeToCalendarEventType(props.workType as WorkType))}`
    const segments: Segment[] = []
    const beforeDuration: number = before.reduce((total, task) => total + task.duration, 0)
    const afterDuration: number = after.reduce((total, task) => total + task.duration, 0)
    if (beforeDuration > 0) {
        segments.push({
            start: start - beforeDuration,
            end: start,
            label: before.map((task) => task.name).join(' · '),
            color: styles.Preparation
        })
    }
    if (valid) {
        if (props.hasPause) {
            segments.push({ start, end: pauseStart, label: t(props.workType), color: workColor })
            segments.push({ start: pauseEnd, end, label: t(props.workType), color: workColor })
        } else {
            segments.push({ start, end, label: t(props.workType), color: workColor })
        }
    }
    if (afterDuration > 0) {
        segments.push({
            start: end,
            end: end + afterDuration,
            label: after.map((task) => task.name).join(' · '),
            color: styles.Preparation
        })
    }
    const visibleSegments: Segment[] = segments.filter((segment) => segment.end > segment.start)
    const opening: number = minutes(props.opening)
    const closing: number = minutes(props.closing)
    const lower: number = Math.min(
        Number.isFinite(opening) ? opening : start,
        start,
        ...visibleSegments.map((segment) => segment.start)
    )
    const upper: number = Math.max(
        Number.isFinite(closing) ? closing : end,
        end,
        ...visibleSegments.map((segment) => segment.end)
    )
    const eventTimes: number[] = [
        ...new Set([lower, ...visibleSegments.flatMap((segment) => [segment.start, segment.end]), upper])
    ].sort((a, b) => a - b)
    const status: string = !valid
        ? t('workPreviewInvalidTimes')
        : needsTasks && (loading || error)
          ? t(error ? 'workPreviewLoadError' : 'workPreviewLoading')
          : ''

    return (
        <aside className={styles.Preview} aria-label={`${t('workSlotPreview')} — ${props.label}`} aria-live='polite'>
            <DialogHeader title={t('preview')} variant='h6' />
            <div className={styles.Timeline}>
                {!status && (
                    <div className={styles.Bounds}>
                        {eventTimes.map((value) => (
                            <span
                                key={value}
                                className={`${styles.Hour} ${value === lower ? styles.FirstHour : ''} ${value === upper ? styles.LastHour : ''}`}
                                style={
                                    { '--position': `${((value - lower) / (upper - lower)) * 100}%` } as CSSProperties
                                }
                            >
                                {time(value)}
                            </span>
                        ))}
                    </div>
                )}
                <Tooltip {...previewTooltipProps} title={status}>
                    <div className={styles.Bar} aria-label={status || undefined}>
                        {!status &&
                            visibleSegments.map((segment, index) => (
                                <Tooltip
                                    key={index}
                                    {...previewTooltipProps}
                                    title={
                                        <div className={styles.TooltipContent}>
                                            <strong className={styles.TooltipLabel}>
                                                <span className={`${styles.TooltipSwatch} ${segment.color}`} />
                                                {segment.label}
                                            </strong>
                                            <span className={styles.TooltipTime}>
                                                {time(segment.start)} – {time(segment.end)}
                                            </span>
                                        </div>
                                    }
                                >
                                    <div
                                        className={`${styles.Segment} ${segment.color} ${visibleSegments[index - 1]?.end === segment.start ? styles.JoinedStart : ''} ${visibleSegments[index + 1]?.start === segment.end ? styles.JoinedEnd : ''}`}
                                        style={
                                            {
                                                '--offset': `${((segment.start - lower) / (upper - lower)) * 100}%`,
                                                '--length': `${((segment.end - segment.start) / (upper - lower)) * 100}%`
                                            } as CSSProperties
                                        }
                                    />
                                </Tooltip>
                            ))}
                    </div>
                </Tooltip>
            </div>
        </aside>
    )
}

export default WorkPreview
